<template>
  <div class="searchable-user-select" v-click-outside="close">
    <div
        class="searchable-user-select__control"
        :class="{ 'searchable-user-select__control--open': open, 'searchable-user-select__control--disabled': disabled }"
        @click="toggle"
    >
      <input
          ref="searchInput"
          v-model="search"
          type="text"
          class="searchable-user-select__input"
          :placeholder="selectedLabel || placeholder"
          :disabled="disabled"
          autocomplete="off"
          @focus="open = true"
          @input="open = true"
      />
      <span
          v-if="value && !open"
          class="searchable-user-select__clear"
          @click.stop="clearSelection"
      >
        &times;
      </span>
      <span class="searchable-user-select__arrow">&#9662;</span>
    </div>

    <ul
        v-if="open"
        class="searchable-user-select__dropdown"
    >
      <li
          class="searchable-user-select__option"
          :class="{ 'searchable-user-select__option--selected': !value }"
          @mousedown.prevent="selectUser('')"
      >
        {{ $t('No user assigned') }}
      </li>

      <li
          v-for="user in filteredUsers"
          :key="user.id"
          class="searchable-user-select__option"
          :class="{ 'searchable-user-select__option--selected': value === user.id }"
          @mousedown.prevent="selectUser(user.id)"
      >
        {{ user.name }}
        <span v-if="user.email" class="searchable-user-select__email">{{ user.email }}</span>
      </li>

      <li
          v-if="filteredUsers.length === 0"
          class="searchable-user-select__no-results"
      >
        {{ $t('No users found') }}
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'SearchableUserSelect',

  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (event) => {
          if (!el.contains(event.target)) {
            binding.value();
          }
        };
        document.addEventListener('mousedown', el._clickOutside);
      },
      unbind(el) {
        document.removeEventListener('mousedown', el._clickOutside);
      }
    }
  },

  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    users: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Search or select a user…'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      open: false,
      search: ''
    };
  },

  computed: {
    selectedLabel() {
      if (!this.value) return '';
      const user = this.users.find(u => u.id === this.value);
      return user ? user.name : '';
    },

    filteredUsers() {
      if (!this.search.trim()) return this.users;
      const term = this.search.toLowerCase();
      return this.users.filter(user => {
        const name = (user.name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        return name.includes(term) || email.includes(term);
      });
    }
  },

  watch: {
    value() {
      this.search = '';
    }
  },

  methods: {
    toggle() {
      if (this.disabled) return;
      this.open = !this.open;
      if (this.open) {
        this.$nextTick(() => this.$refs.searchInput.focus());
      }
    },

    close() {
      this.open = false;
      this.search = '';
    },

    selectUser(id) {
      this.$emit('input', id);
      this.open = false;
      this.search = '';
    },

    clearSelection() {
      this.$emit('input', '');
    }
  }
};
</script>

<style scoped lang="scss">
.searchable-user-select {
  position: relative;
  width: 100%;
}

.searchable-user-select__control {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 7px;
  background: rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease,
      background 0.15s ease;
}

.searchable-user-select__control:hover {
  border-color: rgba(255, 255, 255, 0.32);
}

.searchable-user-select__control--open {
  border-color: rgba(255, 255, 255, 0.65);
  background: rgba(0, 0, 0, 0.28);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.08);
}

.searchable-user-select__control--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.searchable-user-select__input {
  flex: 1;
  min-width: 0;
  padding: 10px 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
}

.searchable-user-select__clear {
  padding: 0 6px;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
}

.searchable-user-select__arrow {
  margin-left: 6px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
}

.searchable-user-select__dropdown {
  position: absolute;
  z-index: 1000;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 220px;
  margin: 0;
  padding: 4px 0;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 7px;
  background: #1e1e2e;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  list-style: none;
  overflow-y: auto;
}

.searchable-user-select__option {
  padding: 9px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.1s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
}

.searchable-user-select__option--selected {
  background: rgba(255, 255, 255, 0.12);
  font-weight: 600;
}

.searchable-user-select__email {
  margin-left: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
}

.searchable-user-select__no-results {
  padding: 12px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
  text-align: center;
}
</style>



